/**
 * Offline boot (#377): proactively store the boot-critical API GETs in the service worker's
 * runtime cache while online, so a single online visit is enough to make the app boot offline.
 *
 * Why it's needed: the Fondation boot gate does `fetch('/api/core/settings')` and *reloads* the
 * page when it fails — offline that becomes a loop on the "Impossible de se connecter au serveur"
 * screen. The SW's NetworkFirst rule only fills the `parcours-api` cache once the (updated) SW
 * controls the page, which doesn't reliably happen on the first load after a deploy. Writing the
 * responses into that same cache by hand removes the timing dependency: after one online load,
 * `/core/settings` is cached, so the next offline load resolves it from cache and reaches Présences.
 */

// Must match the runtimeCaching `cacheName` in vite.config.ts (Workbox uses it verbatim).
const API_CACHE = 'parcours-api';

// The boot-critical GETs the Fondation startup depends on:
//   /core/settings  — the boot gate hard-fails (reload loop) without it
//   /core/me        — restoreOidcSession() fetches it to restore the session; if it throws
//                     offline, isUserLoaded stays false and the app falls back to the login
//                     screen, so a logged-in teacher never reaches Présences offline
//   /privacy/config — anonymous privacy links (non-fatal, but cheap to warm)
// Same paths the SW NetworkFirst rule matches; warming them by URL keeps the cache keys aligned.
// /core/me needs the session cookie (sent via credentials: 'include') and returns 401 when
// signed out — that's skipped by the res.ok check below, so warming is always safe.
const BOOT_PATHS = ['/core/settings', '/core/me', '/privacy/config'];

/**
 * Fetch the boot-critical endpoints and put them in the offline cache. No-op when offline, when
 * Cache Storage is unavailable, or for any endpoint that fails — best-effort warming only.
 *
 * @param apiUrl The SDK API base (same value passed to `sdkOptions.apiUrl`); falls back to `/api`.
 */
export async function warmOfflineApiCache(apiUrl: string | undefined): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  if (typeof caches === 'undefined') return;
  const base = apiUrl || '/api';
  try {
    const cache = await caches.open(API_CACHE);
    await Promise.all(BOOT_PATHS.map(async (path) => {
      const url = `${base}${path}`;
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) await cache.put(url, res.clone());
      } catch {
        /* offline mid-warm or endpoint unavailable — ignore */
      }
    }));
  } catch {
    /* Cache Storage unavailable (e.g. private mode) — ignore */
  }
}
